import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import DataTable from "../carbon/DataTable/DataTable";
import EmptyState from "../carbon/EmptyState/EmptyState";
import { NetworkError } from "..";
import { useOpportunities } from "../../hooks/useOpportunityDiscovery";
import { CategoryType } from "../../types";
import {
  buildOpportunityRows,
  opportunityListHeaders,
} from "./opportunityListRows";

// Fixed server-side page size — the pre-Carbon component hardcoded this same literal at its own
// `useOpportunities(type, page, 10, ...)` call site, not user-toggleable.
const PAGE_SIZE = 10;

interface OpportunityTableProps {
  type: CategoryType;
  selectedFilters: string[];
  page: number;
  onPageChange: (page: number) => void;
}

/**
 * Rebuilt on Carbon `DataTable` (Task 4, Opportunity Discovery + AI Profiling flow). Replaces the
 * pre-Carbon plain `<table>` + `OpportunityRow` and their Tailwind markup wholesale, same pattern
 * `DeliveriesTable.tsx` set for the Deliveries flow. Rows are read straight off
 * `data.content` — an already server-paged, server-filtered page
 * (`GET /api/opportunities/{type}` via `useOpportunities`) — never re-filtered client-side.
 */
const OpportunityTable = ({
  type,
  selectedFilters,
  page,
  onPageChange,
}: OpportunityTableProps) => {
  const { t } = useTranslation();
  const { data, isLoading, isFetching, error } = useOpportunities(
    type,
    page,
    PAGE_SIZE,
    selectedFilters
  );

  const headers = useMemo(() => opportunityListHeaders(t), [t]);
  const rows = useMemo(() => buildOpportunityRows(data?.content), [data]);

  if (error) {
    return <NetworkError />;
  }

  return (
    <DataTable
      headers={headers}
      rows={rows}
      // Mirrors the pre-Carbon component's own spinner condition (`isLoading || isFetching`): a
      // page/filter change re-fetches, and the table should skeleton-load through that refetch
      // too, not only on first mount.
      isLoading={isLoading || isFetching}
      pagination={{
        // DataTable's own Pagination (Carbon) is 1-indexed; this screen's paging state — and the
        // `useOpportunities` query it drives — stays 0-indexed to match the API, same boundary
        // conversion `DeliveriesTable.tsx`/`Orders.tsx`/`Suppliers.tsx` use.
        page: page + 1,
        pageSize: PAGE_SIZE,
        totalItems: data?.totalElements ?? 0,
        onPageChange: (newPage) => onPageChange(newPage - 1),
      }}
      empty={<EmptyState title={t("opportunities.noOpportunities")} />}
    />
  );
};

export default OpportunityTable;
